import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Paginated, Role, StaffSummary } from '@foundation/shared';
import { ROLES, ROLE_LABEL_TH } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { STAFF_STATUS_TH, formatDateTime } from '../lib/format';
import { Badge, Empty, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

interface Team {
  id: string;
  name: string;
}

export default function StaffList() {
  const { allowed } = useAuth();
  const [rows, setRows] = useState<StaffSummary[] | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [q, setQ] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api<Paginated<StaffSummary>>(
        `/staff${buildQuery({ q, role, status, limit: 100 })}`,
      );
      setRows(res.data);
    } catch (e) {
      setError(e);
      setRows([]);
    }
  }, [q, role, status]);

  useEffect(() => {
    const t = setTimeout(load, q ? 300 : 0); // หน่วงเฉพาะตอนพิมพ์ค้นหา
    return () => clearTimeout(t);
  }, [load, q]);

  useEffect(() => {
    api<Team[]>('/meta/teams').then(setTeams).catch(() => setTeams([]));
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>จัดการเจ้าหน้าที่</h1>
          <p>เพิ่มเจ้าหน้าที่ กำหนดสิทธิ์และพื้นที่รับผิดชอบ แล้วส่งลิงก์ให้ผูกบัญชี LINE</p>
        </div>
        {allowed('staff:write') && (
          <button onClick={() => setShowCreate(true)}>+ เพิ่มเจ้าหน้าที่</button>
        )}
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field grow">
            <label htmlFor="q">ค้นหา</label>
            <input
              id="q"
              type="search"
              placeholder="ชื่อ รหัสเจ้าหน้าที่ หรือเบอร์โทร"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="role">สิทธิ์</label>
            <select id="role" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL_TH[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="status">สถานะ</label>
            <select id="status" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">ทั้งหมด</option>
              {Object.entries(STAFF_STATUS_TH).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>ไม่พบเจ้าหน้าที่ตามเงื่อนไขที่ค้นหา</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>รหัส</th>
                  <th className="wrap">ชื่อ-สกุล</th>
                  <th>สิทธิ์</th>
                  <th>ทีม</th>
                  <th>สถานะ</th>
                  <th>LINE</th>
                  <th>เข้าใช้ล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.staffCode}</td>
                    <td className="wrap">
                      <Link className="row-link" to={`/staff/${s.id}`}>
                        {s.fullName}
                      </Link>
                      {s.nickname && <span className="muted small"> ({s.nickname})</span>}
                    </td>
                    <td>{ROLE_LABEL_TH[s.role]}</td>
                    <td>{s.teamName ?? '—'}</td>
                    <td>
                      <Badge value={s.status} label={STAFF_STATUS_TH[s.status] ?? s.status} />
                    </td>
                    <td>
                      {s.lineLinked ? (
                        <span className="badge green">ผูกแล้ว</span>
                      ) : (
                        <span className="badge amber">ยังไม่ผูก</span>
                      )}
                    </td>
                    <td className="small muted">{formatDateTime(s.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <CreateStaffModal
          teams={teams}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            void load();
          }}
        />
      )}
    </>
  );
}

function CreateStaffModal({
  teams,
  onClose,
  onCreated,
}: {
  teams: Team[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { me } = useAuth();
  const [form, setForm] = useState({
    staffCode: '',
    fullName: '',
    nickname: '',
    phone: '',
    email: '',
    googleEmail: '',
    role: 'field_officer' as Role,
    teamId: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const canSetAdmin = me?.role === 'super_admin';

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api('/staff', {
        method: 'POST',
        idempotent: true,
        body: {
          staffCode: form.staffCode.trim(),
          fullName: form.fullName.trim(),
          nickname: form.nickname.trim() || undefined,
          phone: form.phone.trim() || undefined,
          email: form.email.trim() || undefined,
          googleEmail: form.googleEmail.trim() || undefined,
          role: form.role,
          teamId: form.teamId || undefined,
        },
      });
      onCreated();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="เพิ่มเจ้าหน้าที่" onClose={onClose}>
      <form onSubmit={submit}>
        <ErrorAlert error={error} />

        <div className="form-row">
          <div className="field">
            <label htmlFor="staffCode">รหัสเจ้าหน้าที่ *</label>
            <input
              id="staffCode"
              required
              value={form.staffCode}
              onChange={(e) => set('staffCode', e.target.value)}
              placeholder="F-001"
            />
          </div>
          <div className="field">
            <label htmlFor="fullName">ชื่อ-สกุล *</label>
            <input
              id="fullName"
              required
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="nickname">ชื่อเล่น</label>
            <input id="nickname" value={form.nickname} onChange={(e) => set('nickname', e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="phone">เบอร์โทร</label>
            <input
              id="phone"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              placeholder="0812345678"
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="email">อีเมลติดต่อ</label>
          <input id="email" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          <div className="hint">ใช้ติดต่อเท่านั้น ไม่เกี่ยวกับการเข้าสู่ระบบ</div>
        </div>

        <div className="field">
          <label htmlFor="googleEmail">อีเมล Google สำหรับเข้าเว็บ admin</label>
          <input
            id="googleEmail"
            type="email"
            value={form.googleEmail}
            onChange={(e) => set('googleEmail', e.target.value)}
            placeholder="ชื่อ@gmail.com"
          />
          <div className="hint">
            กรอกช่องนี้ = ให้สิทธิ์เข้าเว็บ admin ด้วยบัญชี Google นี้ · เว้นว่างได้ถ้าเป็น
            เจ้าหน้าที่ภาคสนามที่ใช้ LINE อย่างเดียว
          </div>
        </div>

        <div className="form-row">
          <div className="field">
            <label htmlFor="newRole">สิทธิ์ *</label>
            <select id="newRole" value={form.role} onChange={(e) => set('role', e.target.value as Role)}>
              {ROLES.filter((r) => canSetAdmin || (r !== 'admin' && r !== 'super_admin')).map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL_TH[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="teamId">ทีม</label>
            <select id="teamId" value={form.teamId} onChange={(e) => set('teamId', e.target.value)}>
              <option value="">ไม่ระบุ</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="alert info small">
          เจ้าหน้าที่ใหม่จะอยู่ในสถานะ &ldquo;รอผูกบัญชี&rdquo; จนกว่าจะเปิดลิงก์ผูกบัญชีใน LINE
          — กำหนดพื้นที่รับผิดชอบได้ที่หน้ารายละเอียดหลังสร้างเสร็จ
        </div>

        <div className="btn-row" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="secondary" onClick={onClose}>
            ยกเลิก
          </button>
          <button type="submit" disabled={busy}>
            {busy ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
